<script setup>
import dayjs from 'dayjs'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { router, menuRoutes, menuRouteEnum } from '@/router'

import { useAllocationConfirm } from '@/composables/allocation-confirm'
import { useDetectDevice } from '@/composables/device'
import {
  calendarViewDefinition,
  calendarViewDefinitions,
  clampHourHeight,
  daysBetween,
  isTimeGridView,
  MINUTES_PER_HOUR,
  resourceColor,
  SCHEDULE_CHUNK_DAYS,
  shiftDate,
  useCalendarFormatters,
  useEventCalendarData,
  visibleRange,
} from '@/composables/event-calendar'
import { pxToRem } from '@/lib/utils'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import {
  ABSENCE_ICON,
  calendarViewEnum,
  eventTypeEnum,
  PROJECT_FALLBACK_COLOR,
  useScheduleStore,
} from '@/stores/schedule.store'

import AllocationConfirmDialog from '@/components/planner/AllocationConfirmDialog.vue'
import CalendarFilters from '@/components/event/calendar/CalendarFilters.vue'
import CalendarMiniMonth from '@/components/event/calendar/CalendarMiniMonth.vue'
import CalendarMonthGrid from '@/components/event/calendar/CalendarMonthGrid.vue'
import CalendarSchedule from '@/components/event/calendar/CalendarSchedule.vue'
import CalendarTimeGrid from '@/components/event/calendar/CalendarTimeGrid.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import NoStockConfirmDialog from '@/components/shared/NoStockConfirmDialog.vue'
import ProjectDetail from '@/components/shared/ProjectDetail.vue'
import StaffDetail from '@/components/shared/StaffDetail.vue'
import StaffMovementsEdit from '@/components/shared/StaffMovementsEdit.vue'
import TopBarToggle from '@/components/shared/TopBarToggle.vue'

const NOW_TICK_MS = 60000
const PAGE_BOTTOM_PADDING = 1
const DEFAULT_SCROLL_HOUR = 8
const DEFAULT_SLOT_MINUTES = 30
const SWIPE_LOCK_PX = 12
const SWIPE_THRESHOLD_PX = 64
const SWIPE_DIRECTION_RATIO = 1.5
const SWIPE_RESISTANCE = 0.5
const TODAY_KEY = 't'
const NEXT_KEYS = Object.freeze(['j', 'n', 'arrowright'])
const PREVIOUS_KEYS = Object.freeze(['k', 'p', 'arrowleft'])

const { t, locale } = useI18n()
const { isMobile } = useDetectDevice()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const scheduleStore = useScheduleStore()
const webSiteStore = useWebSiteStore()
const { state: data, loadAsync } = useEventCalendarData()
const { noStockDialog, confirmAllocationAsync } = useAllocationConfirm()

const formatters = computed(() => useCalendarFormatters(locale.value))

const now = ref(dayjs())
const currentDate = ref(dayjs())
const scheduleDays = ref(SCHEDULE_CHUNK_DAYS)
const hourHeight = ref(null)
const slideDirection = ref('next')
const swipeOffset = ref(0)
const selectedStaffIds = ref([])
const selectedEquipmentIds = ref([])
const selectedProjectIds = ref([])
const miniMonthOpen = ref(false)
const filtersOpen = ref(false)
const newMovement = ref(null)
const contextTarget = ref(null)
const allocationToConfirm = ref(null)
const pendingDelete = ref(null)
const detailProject = ref(null)
const detailStaff = ref(null)
const scheduleRef = ref(null)
const rootRef = ref(null)
const viewportOffset = ref(null)
const timeGridMemory = { scrollTop: null }
let nowTimer = null
let swipe = null

const view = computed(() => (isMobile.value ? webSiteStore.eventSchedulerMobileView : webSiteStore.eventSchedulerView))
const viewDefinition = computed(() => calendarViewDefinition(view.value))
const range = computed(() => visibleRange(view.value, currentDate.value, scheduleDays.value))
const days = computed(() => daysBetween(range.value.start, range.value.end))
const periodKey = computed(() => `${view.value}-${range.value.start.valueOf()}`)

const parseHours = (time) => {
  if (!time) {
    return null
  }
  const [hours, minutes] = time.split(':').map(Number)
  return hours + (minutes || 0) / MINUTES_PER_HOUR
}
const scrollHour = computed(() => Math.floor(parseHours(accountingStore.companyData?.start_scheduler_time) ?? DEFAULT_SCROLL_HOUR))
const slotMinutes = computed(() => accountingStore.companyData?.scheduler_cell_duration || DEFAULT_SLOT_MINUTES)

const byLabel = (a, b) => a.label.localeCompare(b.label)
const serviceOptions = (services) => (services ?? [])
  .map(service => ({ id: service.id, label: planningStore.getServiceName(service), color: resourceColor(service) }))
  .sort(byLabel)
const staffOptions = computed(() => serviceOptions(planningStore.staff))
const equipmentOptions = computed(() => serviceOptions(planningStore.equipments))
const projectOptions = computed(() => planningStore.projects
  .filter(project => !planningStore.isVacationProject(project.id))
  .map(project => ({ id: project.id, label: project.name, color: project.color || PROJECT_FALLBACK_COLOR }))
  .sort(byLabel))
const filtersActive = computed(() => selectedStaffIds.value.length < staffOptions.value.length
  || selectedEquipmentIds.value.length < equipmentOptions.value.length
  || selectedProjectIds.value.length < projectOptions.value.length)

const visibleEvents = computed(() => {
  const staffIds = new Set(selectedStaffIds.value)
  const equipmentIds = new Set(selectedEquipmentIds.value)
  const projectIds = new Set(selectedProjectIds.value)
  return data.events.filter(event => {
    if (event.is_project_root) {
      return projectIds.has(event.project_id)
    }
    if (event.type === eventTypeEnum.ABSENCE) {
      return webSiteStore.showEventVacations && (!event.staff || staffIds.has(event.staff.id))
    }
    const resourceSelected = (!!event.staff && staffIds.has(event.staff.id))
      || (!!event.equipment && equipmentIds.has(event.equipment.id))
    return resourceSelected && projectIds.has(event.project_id)
  })
})

const title = computed(() => {
  const format = formatters.value
  const { start, end } = range.value
  const last = end.subtract(1, 'day')
  if (view.value === calendarViewEnum.DAY && !isMobile.value) {
    return format.dayLong.format(start.toDate())
  }
  if (!isTimeGridView(view.value) || start.isSame(last, 'month') || isMobile.value) {
    return format.monthYear.format((isTimeGridView(view.value) ? start : currentDate.value).toDate())
  }
  if (start.isSame(last, 'year')) {
    return `${format.monthShort.format(start.toDate())} – ${format.monthShortYear.format(last.toDate())}`
  }
  return `${format.monthShortYear.format(start.toDate())} – ${format.monthShortYear.format(last.toDate())}`
})

const viewSlot = (definition) => `view-${definition.value}`
const viewOptions = computed(() => calendarViewDefinitions.map(definition => ({
  value: definition.value,
  label: definition.label(t),
  slot: viewSlot(definition),
})))

const fetchRange = () => {
  if (view.value === calendarViewEnum.SCHEDULE) {
    return range.value
  }
  return {
    start: visibleRange(view.value, shiftDate(view.value, currentDate.value, -1)).start,
    end: visibleRange(view.value, shiftDate(view.value, currentDate.value, 1)).end,
  }
}

const reloadAsync = () => loadAsync({
  ...fetchRange(),
  includeAllocations: view.value !== calendarViewEnum.MONTH,
  includeAbsences: webSiteStore.showEventVacations,
})

const scrollScheduleTo = async(date, behavior) => {
  if (view.value !== calendarViewEnum.SCHEDULE) {
    return
  }
  await nextTick()
  scheduleRef.value?.scrollToDate(date, behavior)
}

const goToAsync = async(date, direction) => {
  slideDirection.value = direction < 0 ? 'prev' : 'next'
  currentDate.value = dayjs(date)
  scheduleDays.value = SCHEDULE_CHUNK_DAYS
  await reloadAsync()
  await scrollScheduleTo(currentDate.value, 'smooth')
}

const navigateAsync = (direction) => goToAsync(shiftDate(view.value, currentDate.value, direction), direction)
const goTodayAsync = () => goToAsync(dayjs(), dayjs().isBefore(currentDate.value, 'day') ? -1 : 1)

const onPickDateAsync = (day) => {
  miniMonthOpen.value = false
  return goToAsync(day, day.isBefore(currentDate.value, 'day') ? -1 : 1)
}

const setView = (value) => {
  if (isMobile.value) {
    webSiteStore.eventSchedulerMobileView = value
  } else {
    webSiteStore.eventSchedulerView = value
  }
}

const onViewChangeAsync = async(value) => {
  if (value === view.value) {
    return
  }
  slideDirection.value = value < view.value ? 'prev' : 'next'
  setView(value)
  scheduleDays.value = SCHEDULE_CHUNK_DAYS
  await reloadAsync()
  await scrollScheduleTo(currentDate.value)
}

const onDayClickAsync = (day) => {
  setView(calendarViewEnum.DAY)
  return goToAsync(day, 1)
}

const onLoadMoreAsync = async() => {
  if (data.loading) {
    return
  }
  scheduleDays.value += SCHEDULE_CHUNK_DAYS
  await reloadAsync()
}

const onShowAbsencesChangeAsync = async(value) => {
  webSiteStore.showEventVacations = value
  await reloadAsync()
}

const onZoom = (height) => {
  hourHeight.value = clampHourHeight(height)
}

const canEditEvent = (event) => (event.type === eventTypeEnum.ABSENCE
  ? event.service_id === webSiteStore.user?.service_id
    || webSiteStore.can(webSiteStore.permissionFlagEnum.APPROVALS)
  : webSiteStore.can(webSiteStore.permissionFlagEnum.PLANNING))

const onEventChangeAsync = async({ event, start, end }) => {
  const target = data.events.find(item => item.id === event.id)
  if (target) {
    target.startAt = start
    target.endAt = end
  }
  try {
    await scheduleStore.upsertEventAsync({
      id: event.id,
      start: start.toISOString(),
      end: end.toISOString(),
      ...(event.type === eventTypeEnum.ABSENCE
        ? { confirmed: event.confirmed }
        : { update_planned: !event.project?.lock_planned }),
    })
  } finally {
    await reloadAsync()
  }
}

const openNewMovement = (vacation, range = {}) => {
  newMovement.value = {
    vacation,
    start: range.start?.toISOString() ?? null,
    end: range.end?.toISOString() ?? null,
    startDate: range.day?.format('YYYY-MM-DD') ?? null,
  }
}

const onCreate = (range) => openNewMovement(false, range)

const onMovementSavedAsync = async() => {
  newMovement.value = null
  await reloadAsync()
}

const onEventClick = (event) => {
  if (event.type !== eventTypeEnum.ABSENCE) {
    detailProject.value = event.project ?? null
  } else if (event.staff) {
    detailStaff.value = event.staff
  }
}

const onProjectDetailUpdateAsync = async() => {
  await planningStore.fetchProjectsAsync({ all: true }, true)
  await reloadAsync()
}

const onStaffDetailUpdateAsync = async() => {
  detailStaff.value = null
  await reloadAsync()
}

const canAssignServices = computed(() => webSiteStore.can(webSiteStore.permissionFlagEnum.PLANNING))
const canApproveAbsences = computed(() => webSiteStore.can(webSiteStore.permissionFlagEnum.APPROVALS))

const onAllocationConfirmedAsync = async() => {
  allocationToConfirm.value = null
  await reloadAsync()
}

const confirmEventAllocationAsync = async(event) => {
  const service = planningStore.getServiceById(event.service_id)
  if (!service || planningStore.isUnitaryService(service)) {
    await confirmAllocationAsync(event.service_id, event.project_id, event.budget_document_row_id, reloadAsync)
    return
  }
  const row = await accountingStore.fetchBudgetDocumentRowByIdAsync(event.budget_document_row_id)
  allocationToConfirm.value = {
    serviceId: event.service_id,
    projectId: event.project_id,
    budgetDocumentRowId: event.budget_document_row_id,
    serviceName: event.title,
    quantity: Number(row.quantity),
    projectStart: event.project?.start ?? null,
  }
}

const cancelEventAllocationAsync = async(event) => {
  await scheduleStore.cancelAllocationAsync(event.service_id, event.project_id, event.budget_document_row_id)
  await reloadAsync()
}

const setAbsenceApprovalAsync = async(event, confirmed) => {
  await scheduleStore.upsertEventAsync({ id: event.id, confirmed })
  await reloadAsync()
}

const askDeleteEvent = (event) => {
  pendingDelete.value = {
    message: t('common.confirmDeleteMessage', { item: event.title || t('common.staff.absence') }),
    action: async() => {
      await scheduleStore.deleteEventAsync(event.id, { updatePlanned: event.project_id ? !event.project?.lock_planned : false })
      await reloadAsync()
    },
  }
}

const onDeleteConfirmedAsync = async() => {
  const { action } = pendingDelete.value
  pendingDelete.value = null
  await action()
}

const onStageContextMenu = (e) => {
  const eventId = e.target.closest('[data-event-id]')?.dataset.eventId
  const day = e.target.closest('[data-day]')?.dataset.day
  contextTarget.value = {
    event: eventId ? data.events.find(item => String(item.id) === eventId) ?? null : null,
    day: day ? dayjs(day) : null,
  }
}

const openProjectAction = (project) => project && {
  key: 'openProject',
  icon: 'mdi-briefcase-outline',
  label: t('common.openProject'),
  action: () => { detailProject.value = project },
}

const goToDayAction = (day) => ({
  key: 'goToDay',
  icon: 'mdi-view-day-outline',
  label: t('common.goToDay'),
  action: () => onDayClickAsync(day.startOf('day')),
})

const deleteEventAction = (event) => canEditEvent(event) && {
  key: 'delete',
  icon: 'mdi-delete-outline',
  color: 'negative',
  label: t('common.delete'),
  action: () => askDeleteEvent(event),
}

const allocationActions = (event) => ({
  primary: [
    canAssignServices.value && (event.confirmed
      ? { key: 'cancel', icon: 'mdi-close-circle-outline', label: t('common.cancelAllocation'), action: () => cancelEventAllocationAsync(event) }
      : { key: 'confirm', icon: 'mdi-check-circle-outline', color: 'positive', label: t('common.confirmAllocation'), action: () => confirmEventAllocationAsync(event) }),
    openProjectAction(event.project),
  ],
  secondary: [goToDayAction(event.startAt)],
  danger: [deleteEventAction(event)],
})

const absenceActions = (event) => ({
  primary: [
    canApproveAbsences.value && (event.confirmed
      ? { key: 'revert', icon: 'mdi-undo-variant', label: t('common.restore'), action: () => setAbsenceApprovalAsync(event, false) }
      : { key: 'approve', icon: 'mdi-check', color: 'positive', label: t('common.approve'), action: () => setAbsenceApprovalAsync(event, true) }),
    event.staff && { key: 'openStaff', icon: 'mdi-account-outline', label: t('common.open'), action: () => { detailStaff.value = event.staff } },
  ],
  secondary: [goToDayAction(event.startAt)],
  danger: [deleteEventAction(event)],
})

const projectRootActions = (event) => ({
  primary: [
    canAssignServices.value && { key: 'confirmAll', icon: 'mdi-check-circle-outline', color: 'positive', label: t('common.confirmAllAllocations'), action: () => confirmAllocationAsync(null, event.project_id, null, reloadAsync) },
    canAssignServices.value && { key: 'cancelAll', icon: 'mdi-close-circle-outline', label: t('common.cancelAllAllocations'), action: () => cancelEventAllocationAsync({ ...event, service_id: null, budget_document_row_id: null }) },
    openProjectAction(event.project),
  ],
  secondary: [],
  danger: [],
})

const dayActions = (day) => ({
  primary: [
    { key: 'addEvent', icon: 'mdi-calendar-plus', label: t('common.addEvent'), action: () => openNewMovement(false, { day }) },
    { key: 'addAbsence', icon: ABSENCE_ICON, label: t('common.addAbsence'), action: () => openNewMovement(true, { day }) },
  ],
  secondary: [view.value !== calendarViewEnum.DAY && goToDayAction(day)],
  danger: [],
})

const targetActions = ({ event, day }) => {
  if (event?.is_project_root) return projectRootActions(event)
  if (event?.type === eventTypeEnum.ABSENCE) return absenceActions(event)
  if (event) return allocationActions(event)
  return day ? dayActions(day) : { primary: [], secondary: [], danger: [] }
}

const contextTitle = computed(() => contextTarget.value?.event?.title
  ?? (contextTarget.value?.day ? formatters.value.dayLong.format(contextTarget.value.day.toDate()) : null))

const contextSections = computed(() => {
  const general = [{ key: 'today', icon: 'mdi-calendar-today', label: t('common.today'), action: goTodayAsync }]
  const target = contextTarget.value
  if (!target) return [general]
  const { primary, secondary, danger } = targetActions(target)
  return [primary, secondary, general, danger]
    .map(section => section.filter(Boolean))
    .filter(section => section.length)
})

const createActions = computed(() => [
  { id: 'add-event', icon: 'mdi-calendar-plus', label: t('common.event', 1), onClick: () => openNewMovement(false) },
  { id: 'add', icon: ABSENCE_ICON, label: t('common.staff.absence'), onClick: () => openNewMovement(true) },
  {
    id: 'add-project',
    icon: 'mdi-folder-plus',
    label: t('common.project', 1),
    onClick: () => {
      webSiteStore.editedProjectId = 0
      router.push(menuRoutes[menuRouteEnum.PROJECTS])
    },
  },
])

const swipeStyle = computed(() => (swipeOffset.value
  ? { transform: `translateX(${pxToRem(swipeOffset.value * SWIPE_RESISTANCE)}rem)`, transition: 'none' }
  : undefined))

const onSwipeStart = (e) => {
  swipe = e.touches.length === 1 && view.value !== calendarViewEnum.SCHEDULE
    ? { x: e.touches[0].clientX, y: e.touches[0].clientY, horizontal: null }
    : null
}

const onSwipeMove = (e) => {
  if (!swipe || e.touches.length !== 1) {
    swipe = null
    swipeOffset.value = 0
    return
  }
  const dx = e.touches[0].clientX - swipe.x
  const dy = e.touches[0].clientY - swipe.y
  if (swipe.horizontal === null && Math.hypot(dx, dy) > SWIPE_LOCK_PX) {
    swipe.horizontal = Math.abs(dx) > Math.abs(dy) * SWIPE_DIRECTION_RATIO
  }
  if (swipe.horizontal) {
    swipeOffset.value = dx
  }
}

const onSwipeEnd = () => {
  const offset = swipeOffset.value
  swipe = null
  swipeOffset.value = 0
  if (Math.abs(offset) > SWIPE_THRESHOLD_PX) {
    navigateAsync(offset < 0 ? 1 : -1)
  }
}

const isTypingTarget = (target) => !!target?.closest?.('input, textarea, select, [contenteditable="true"]')

const onKeydown = (e) => {
  if (isMobile.value || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) {
    return
  }
  if (isTypingTarget(e.target) || document.querySelector('.q-dialog')) {
    return
  }
  const key = e.key.toLowerCase()
  const definition = calendarViewDefinitions.find(item => item.shortcut === key)
  if (definition) {
    onViewChangeAsync(definition.value)
  } else if (key === TODAY_KEY) {
    goTodayAsync()
  } else if (NEXT_KEYS.includes(key)) {
    navigateAsync(1)
  } else if (PREVIOUS_KEYS.includes(key)) {
    navigateAsync(-1)
  }
}

const ensureCollectionsAsync = () => Promise.all([
  !planningStore.projects.length && planningStore.fetchProjectsAsync({ all: true }, true),
  !planningStore.staff.length && planningStore.fetchStaffsAsync({ all: true }, true),
  !planningStore.equipments.length && planningStore.fetchEquipmentsAsync({ all: true }, true),
].filter(Boolean))

onMounted(async() => {
  webSiteStore.fabActions = createActions.value
  if (webSiteStore.eventSchedulerDate) {
    currentDate.value = dayjs(webSiteStore.eventSchedulerDate)
    webSiteStore.eventSchedulerDate = null
  }
  if (webSiteStore.eventSchedulerVacation) {
    openNewMovement(true)
    webSiteStore.eventSchedulerVacation = null
  }
  nowTimer = setInterval(() => {
    now.value = dayjs()
  }, NOW_TICK_MS)
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('resize', measureViewportOffset)
  measureViewportOffset()

  await ensureCollectionsAsync()
  selectedProjectIds.value = projectOptions.value.map(option => option.id)
  selectedStaffIds.value = staffOptions.value.map(option => option.id)
  await reloadAsync()
  await scrollScheduleTo(currentDate.value)
})

const documentTop = (element) => {
  let top = 0
  for (let node = element; node; node = node.offsetParent) {
    top += node.offsetTop
  }
  return top
}

const measureViewportOffset = () => {
  if (rootRef.value) {
    viewportOffset.value = pxToRem(documentTop(rootRef.value)) + (isMobile.value ? 0 : PAGE_BOTTOM_PADDING)
  }
}

watch(() => webSiteStore.topBarHidden, () => nextTick(measureViewportOffset), { flush: 'post' })

const rootStyle = computed(() => (viewportOffset.value === null
  ? undefined
  : { height: `calc(100dvh - ${viewportOffset.value}rem)` }))

onBeforeUnmount(() => {
  clearInterval(nowTimer)
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', measureViewportOffset)
})
</script>
<template>
  <div
    ref="rootRef"
    class="event-calendar"
    :class="{ 'event-calendar--mobile': isMobile }"
    :style="rootStyle"
  >
    <q-dialog
      :model-value="newMovement !== null"
      :maximized="isMobile"
      class="dialog-xs"
      @hide="newMovement = null"
    >
      <staff-movements-edit
        v-if="newMovement"
        :service="webSiteStore.user?.service_id ?? null"
        :vacation="newMovement.vacation"
        :start="newMovement.start"
        :end="newMovement.end"
        :start-date="newMovement.startDate"
        @update="onMovementSavedAsync"
        @cancel="newMovement = null"
      />
    </q-dialog>
    <confirm-dialog
      :model-value="pendingDelete !== null"
      :title="t('common.confirmDelete')"
      :message="pendingDelete?.message ?? ''"
      :label="t('common.delete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmedAsync"
      @cancel="pendingDelete = null"
    />
    <no-stock-confirm-dialog v-model="noStockDialog" />
    <allocation-confirm-dialog
      v-if="allocationToConfirm"
      :model-value="true"
      :service-id="allocationToConfirm.serviceId"
      :project-id="allocationToConfirm.projectId"
      :budget-document-row-id="allocationToConfirm.budgetDocumentRowId"
      :service-name="allocationToConfirm.serviceName"
      :quantity="allocationToConfirm.quantity"
      :project-start="allocationToConfirm.projectStart"
      @update:model-value="value => { if (!value) allocationToConfirm = null }"
      @confirmed="onAllocationConfirmedAsync"
    />
    <q-dialog
      :model-value="detailProject !== null"
      :maximized="isMobile"
      class="dialog-xs"
      @hide="detailProject = null"
    >
      <project-detail
        v-if="detailProject"
        :model-value="detailProject"
        @update="onProjectDetailUpdateAsync"
        @cancel="detailProject = null"
      />
    </q-dialog>
    <q-dialog
      :model-value="detailStaff !== null"
      :maximized="isMobile"
      class="dialog-xs"
      @hide="detailStaff = null"
    >
      <staff-detail
        v-if="detailStaff"
        :model-value="detailStaff"
        tab="holidays"
        @update="onStaffDetailUpdateAsync"
        @cancel="detailStaff = null"
      />
    </q-dialog>
    <q-dialog
      v-if="isMobile"
      v-model="filtersOpen"
      maximized
    >
      <q-card class="dialog-card-flex cal-scope">
        <navigate-back
          :text="t('common.calendars')"
          @cancel="filtersOpen = false"
        />
        <q-card-section class="scroll col q-pa-sm">
          <calendar-filters
            :staff-options="staffOptions"
            :staff-ids="selectedStaffIds"
            :equipment-options="equipmentOptions"
            :equipment-ids="selectedEquipmentIds"
            :project-options="projectOptions"
            :project-ids="selectedProjectIds"
            :show-absences="webSiteStore.showEventVacations"
            @update:staff-ids="ids => selectedStaffIds = ids"
            @update:equipment-ids="ids => selectedEquipmentIds = ids"
            @update:project-ids="ids => selectedProjectIds = ids"
            @update:show-absences="onShowAbsencesChangeAsync"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <aside
      v-if="!isMobile"
      class="event-calendar__sidebar"
      :class="{ 'event-calendar__sidebar--collapsed': !webSiteStore.eventCalendarSidebarOpen }"
    >
      <div class="event-calendar__sidebar-inner">
        <calendar-mini-month
          :model-value="currentDate"
          :range-start="range.start"
          :range-end="range.end"
          @update:model-value="onPickDateAsync"
        />
        <div class="event-calendar__sidebar-title">
          {{ t('common.calendars') }}
        </div>
        <calendar-filters
          :staff-options="staffOptions"
          :staff-ids="selectedStaffIds"
          :equipment-options="equipmentOptions"
          :equipment-ids="selectedEquipmentIds"
          :project-options="projectOptions"
          :project-ids="selectedProjectIds"
          :show-absences="webSiteStore.showEventVacations"
          @update:staff-ids="ids => selectedStaffIds = ids"
          @update:equipment-ids="ids => selectedEquipmentIds = ids"
          @update:project-ids="ids => selectedProjectIds = ids"
          @update:show-absences="onShowAbsencesChangeAsync"
        />
      </div>
    </aside>

    <main class="event-calendar__main">
      <div v-if="!isMobile" class="event-calendar__toolbar">
        <q-btn
          flat
          round
          dense
          icon="mdi-menu"
          color="grey-8"
          @click="webSiteStore.eventCalendarSidebarOpen = !webSiteStore.eventCalendarSidebarOpen"
        />
        <q-btn
          outline
          rounded
          dense
          no-caps
          color="primary"
          icon="mdi-calendar-today"
          :label="t('common.today')"
          class="event-calendar__today-btn"
          @click="goTodayAsync"
        >
          <q-tooltip>{{ formatters.dayLong.format(now.toDate()) }} ({{ TODAY_KEY.toUpperCase() }})</q-tooltip>
        </q-btn>
        <div class="event-calendar__nav">
          <q-btn
            flat
            round
            dense
            size="sm"
            icon="mdi-chevron-left"
            @click="navigateAsync(-1)"
          >
            <q-tooltip>{{ t('common.previous') }} (K)</q-tooltip>
          </q-btn>
          <q-btn
            flat
            round
            dense
            size="sm"
            icon="mdi-chevron-right"
            @click="navigateAsync(1)"
          >
            <q-tooltip>{{ t('common.next') }} (J)</q-tooltip>
          </q-btn>
        </div>
        <transition name="cal-title" mode="out-in">
          <h2 :key="title" class="event-calendar__title">
            {{ title }}
          </h2>
        </transition>
        <q-space />
        <q-btn-toggle
          :model-value="view"
          :options="viewOptions"
          class="event-calendar__views"
          no-caps
          unelevated
          dense
          toggle-color="primary"
          text-color="grey-8"
          @update:model-value="onViewChangeAsync"
        >
          <template
            v-for="definition in calendarViewDefinitions"
            :key="definition.value"
            #[viewSlot(definition)]
          >
            <q-tooltip>
              {{ definition.label(t) }} ({{ definition.shortcut.toUpperCase() }})
            </q-tooltip>
          </template>
        </q-btn-toggle>
        <q-btn
          flat
          round
          dense
          icon="mdi-plus"
          color="grey-8"
        >
          <q-tooltip>{{ t('common.add') }}</q-tooltip>
          <q-menu
            anchor="bottom right"
            self="top right"
            auto-close
          >
            <q-list>
              <q-item
                v-for="action in createActions"
                :key="action.id"
                clickable
                @click="action.onClick"
              >
                <q-item-section avatar>
                  <q-icon :name="action.icon" />
                </q-item-section>
                <q-item-section>{{ action.label }}</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </div>

      <template v-else>
        <div class="event-calendar__toolbar event-calendar__toolbar--mobile">
          <button
            type="button"
            class="event-calendar__title-btn"
            @click="miniMonthOpen = !miniMonthOpen"
          >
            <transition name="cal-title" mode="out-in">
              <span :key="title" class="event-calendar__title">{{ title }}</span>
            </transition>
            <q-icon
              name="mdi-menu-down"
              size="sm"
              class="event-calendar__caret"
              :class="{ 'event-calendar__caret--open': miniMonthOpen }"
            />
          </button>
          <q-space />
          <button
            type="button"
            class="event-calendar__today-tile"
            :aria-label="t('common.today')"
            @click="goTodayAsync"
          >
            <span class="event-calendar__today-band">{{ formatters.monthShort.format(now.toDate()) }}</span>
            <span class="event-calendar__today-day">{{ now.date() }}</span>
          </button>
          <div class="event-calendar__mobile-actions">
            <q-btn
              flat
              dense
              no-caps
              class="event-calendar__mobile-action"
            >
              <q-icon :name="viewDefinition.icon" size="xs" />
              <span class="event-calendar__mobile-action-label">{{ viewDefinition.label(t) }}</span>
              <q-icon name="mdi-chevron-down" size="1rem" class="event-calendar__mobile-action-caret" />
              <q-menu
                anchor="bottom right"
                self="top right"
                :offset="[0, 6]"
                auto-close
              >
                <q-list class="event-calendar__view-menu">
                  <q-item
                    v-for="definition in calendarViewDefinitions"
                    :key="definition.value"
                    clickable
                    :active="definition.value === view"
                    active-class="event-calendar__view-item--active"
                    @click="onViewChangeAsync(definition.value)"
                  >
                    <q-item-section avatar>
                      <q-icon :name="definition.icon" />
                    </q-item-section>
                    <q-item-section>{{ definition.label(t) }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
            <span class="event-calendar__mobile-divider" />
            <q-btn
              flat
              dense
              icon="mdi-tune-variant"
              class="event-calendar__mobile-action event-calendar__mobile-action--icon"
              :class="{ 'event-calendar__mobile-action--active': filtersActive }"
              @click="filtersOpen = true"
            />
            <span class="event-calendar__mobile-divider" />
            <top-bar-toggle class="event-calendar__mobile-action event-calendar__mobile-action--icon" />
          </div>
        </div>
        <q-slide-transition>
          <div v-show="miniMonthOpen" class="event-calendar__mini-mobile">
            <calendar-mini-month
              :model-value="currentDate"
              :range-start="range.start"
              :range-end="range.end"
              @update:model-value="onPickDateAsync"
            />
          </div>
        </q-slide-transition>
      </template>

      <div
        class="event-calendar__stage"
        @touchstart.passive="onSwipeStart"
        @touchmove.passive="onSwipeMove"
        @touchend.passive="onSwipeEnd"
        @touchcancel.passive="onSwipeEnd"
        @contextmenu.capture="onStageContextMenu"
      >
        <q-menu
          context-menu
          touch-position
          auto-close
        >
          <q-list dense class="event-calendar__menu">
            <q-item-label v-if="contextTitle" header class="event-calendar__menu-header">
              <div class="event-calendar__menu-title">
                {{ contextTitle }}
              </div>
              <div v-if="contextTarget?.event" class="event-calendar__menu-caption">
                {{ contextTarget.event.subtitle || contextTarget.event.status }}
              </div>
            </q-item-label>
            <template v-for="(section, index) in contextSections" :key="index">
              <q-separator v-if="index > 0 || contextTitle" />
              <q-item
                v-for="item in section"
                :key="item.key"
                clickable
                @click="item.action"
              >
                <q-item-section avatar class="event-calendar__menu-icon">
                  <q-icon :name="item.icon" :color="item.color ?? 'grey-8'" size="1.125rem" />
                </q-item-section>
                <q-item-section :class="item.color ? `text-${item.color}` : undefined">
                  {{ item.label }}
                </q-item-section>
              </q-item>
            </template>
          </q-list>
        </q-menu>
        <q-linear-progress
          v-if="data.loading"
          indeterminate
          color="primary"
          size="0.125rem"
          class="event-calendar__loading"
        />
        <transition :name="`cal-slide-${slideDirection}`">
          <div
            :key="periodKey"
            class="event-calendar__page"
            :style="swipeStyle"
          >
            <calendar-time-grid
              v-if="isTimeGridView(view)"
              :days="days"
              :events="visibleEvents"
              :now="now"
              :scroll-hour="scrollHour"
              :slot-minutes="slotMinutes"
              :hour-height="hourHeight"
              :initial-scroll-top="timeGridMemory.scrollTop"
              :can-edit="canEditEvent"
              :compact="isMobile"
              @event-click="onEventClick"
              @create="onCreate"
              @event-change="onEventChangeAsync"
              @day-click="onDayClickAsync"
              @scroll="top => timeGridMemory.scrollTop = top"
              @zoom="onZoom"
            />
            <calendar-month-grid
              v-else-if="view === calendarViewEnum.MONTH"
              :month="currentDate"
              :range-start="range.start"
              :range-end="range.end"
              :events="visibleEvents"
              :now="now"
              :compact="isMobile"
              @event-click="onEventClick"
              @create="onCreate"
              @day-click="onDayClickAsync"
            />
            <calendar-schedule
              v-else
              ref="scheduleRef"
              :range-start="range.start"
              :range-end="range.end"
              :events="visibleEvents"
              :now="now"
              :loading="data.loading"
              @event-click="onEventClick"
              @load-more="onLoadMoreAsync"
            />
          </div>
        </transition>
      </div>
    </main>
  </div>
</template>
<style lang="scss">
.event-calendar,
.cal-scope {
  --cal-surface: #{$white};
  --cal-surface-alt: color-mix(in srgb, var(--q-primary) 3%, var(--cal-surface));
  --cal-border: color-mix(in srgb, var(--q-primary) 15%, transparent);
  --cal-line: rgba(0, 0, 0, 0.06);
  --cal-rest-day: color-mix(in srgb, var(--q-primary) 4%, transparent);
  --cal-today: color-mix(in srgb, var(--q-primary) 5%, transparent);
  --cal-past: rgba(0, 0, 0, 0.018);
  --cal-text: #1f1d2b;
  --cal-text-muted: #8b879a;
  --cal-now: var(--q-secondary);
}

.body--dark .event-calendar,
.body--dark .cal-scope {
  --cal-surface: #{$dark};
  --cal-border: color-mix(in srgb, var(--q-primary) 30%, transparent);
  --cal-line: rgba(255, 255, 255, 0.07);
  --cal-rest-day: color-mix(in srgb, var(--q-primary) 8%, transparent);
  --cal-past: rgba(255, 255, 255, 0.02);
  --cal-text: #ecebf2;
  --cal-text-muted: #9a96aa;
}

// --- Event visual, shared by every calendar view ---
.cal-event {
  --event-color: var(--q-primary);
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  overflow: hidden;
  padding: 0 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid color-mix(in srgb, var(--event-color) 45%, transparent);
  background: color-mix(in srgb, var(--event-color) 18%, var(--cal-surface));
  color: color-mix(in srgb, var(--event-color) 70%, var(--cal-text));
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.25;
  cursor: pointer;
  user-select: none;
  transition:
    box-shadow 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    color 0.3s ease,
    opacity 0.3s ease;

  &:hover .cal-event__handle {
    opacity: 1;
  }
}

.cal-event__content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
}

.cal-event__title,
.cal-event__subtitle,
.cal-event__time,
.cal-event__status {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cal-event__title {
  font-weight: 600;
}

.cal-event__subtitle,
.cal-event__time,
.cal-event__status {
  font-size: 0.6875rem;
  opacity: 0.85;
}

.cal-event__status {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.125rem;
}

.cal-event__dot {
  flex-shrink: 0;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--event-color);
}

.cal-event--timed {
  z-index: var(--event-layer, 1);
  align-items: flex-start;
  transition:
    box-shadow 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    left 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    width 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    color 0.3s ease,
    opacity 0.3s ease;
  padding: 0.1875rem 0.3125rem;
  border-color: transparent;
  background: linear-gradient(135deg, var(--event-color), color-mix(in srgb, var(--event-color) 60%, white));
  color: white;
  box-shadow: 0 0 0 1px var(--cal-surface);

  .cal-event__dot {
    position: absolute;
    top: 0.3125rem;
    right: 0.3125rem;
    box-shadow: 0 0 0 0.125rem color-mix(in srgb, var(--event-color) 70%, white);
  }

  .cal-event__title {
    padding-right: 0.75rem;
  }
}

.cal-event--compact {
  padding-top: 0.0625rem;
  padding-bottom: 0;
}

.cal-event--editable {
  cursor: grab;
}

.cal-event__handle {
  position: absolute;
  left: 50%;
  bottom: 0.1875rem;
  width: 1.25rem;
  height: 0.25rem;
  transform: translateX(-50%);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.75);
  opacity: 0;
  cursor: ns-resize;
  transition: opacity 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    inset: -0.375rem -0.5rem;
  }
}

.cal-event--bar {
  height: 1.375rem;
  padding: 0 0.5rem;
  border-radius: 0.375rem;
  transition:
    box-shadow 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    top 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    left 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    width 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    color 0.3s ease,
    opacity 0.3s ease;

  .cal-event__subtitle {
    flex-shrink: 1;
  }
}

.cal-event--bar.cal-event--project {
  gap: 0.375rem;
  padding: 0 0.625rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--event-color) 22%, var(--cal-surface));
  border: 1px solid color-mix(in srgb, var(--event-color) 45%, transparent);
  color: color-mix(in srgb, var(--event-color) 70%, var(--cal-text));
  font-weight: 600;
  box-shadow: none;
}

.cal-event--chip {
  height: 1.25rem;
  padding: 0 0.375rem;
  border-color: transparent;
  background: transparent;
  color: var(--cal-text);
  font-weight: 500;
  box-shadow: none;

  &:hover {
    background: color-mix(in srgb, var(--event-color) 12%, transparent);
    box-shadow: none;
  }

  .cal-event__time {
    flex-shrink: 0;
    opacity: 0.7;
  }
}

.cal-event--card {
  position: relative;
  flex-direction: column;
  align-items: stretch;
  gap: 0.125rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.875rem;
  font-size: 0.8125rem;

  &:active {
    transform: scale(0.985);
  }

  .cal-event__time {
    font-size: 0.75rem;
  }
}

.cal-event__card-head {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;

  .cal-event__title {
    flex: 1;
  }
}

.cal-event__span {
  flex-shrink: 0;
  padding: 0.0625rem 0.5rem;
  border-radius: 1rem;
  background: color-mix(in srgb, var(--event-color) 14%, transparent);
  font-size: 0.6875rem;
  font-weight: 600;
}

.cal-event__resources {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.375rem;
}

.cal-event__resource {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  max-width: 100%;
  padding: 0.125rem 0.5rem 0.125rem 0.25rem;
  overflow: hidden;
  border-radius: 1rem;
  background: color-mix(in srgb, var(--event-color) 14%, transparent);
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cal-event__resource-dot {
  flex-shrink: 0;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  box-shadow: 0 0 0 0.125rem var(--cal-surface);
}

.cal-event--unconfirmed {
  background: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--event-color) 24%, var(--cal-surface)) 0 0.375rem,
    color-mix(in srgb, var(--event-color) 12%, var(--cal-surface)) 0.375rem 0.75rem
  );
  border: 1px dashed color-mix(in srgb, var(--event-color) 60%, transparent);
  color: color-mix(in srgb, var(--event-color) 75%, var(--cal-text));
  box-shadow: none;
}

.cal-event--clip-start {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.cal-event--clip-end {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.cal-event--timed.cal-event--clip-start {
  border-radius: 0 0 0.5rem 0.5rem;
}

.cal-event--timed.cal-event--clip-end {
  border-radius: 0.5rem 0.5rem 0 0;
}

.cal-event--drag-source,
.cal-event.cal-event--drag-source:hover {
  opacity: 0.35;
}

.cal-event--preview {
  z-index: 70;
  left: 0.125rem;
  width: calc(100% - var(--event-gutter, 0rem) - 0.25rem);
  opacity: 0.95;
  box-shadow: 0 0.75rem 1.5rem -0.5rem color-mix(in srgb, var(--event-color) 70%, transparent);
  pointer-events: none;
}

.cal-event:hover {
  z-index: 50;
  color: white;
  opacity: 1;
  transform: translateY(-0.125rem);
  // The solid fill is an inset shadow (listed last) so it fades in: a gradient background cannot be transitioned.
  box-shadow:
    0 0 0 1px var(--cal-surface),
    0 0.75rem 1.5rem -0.5rem color-mix(in srgb, var(--event-color) 70%, transparent),
    inset 0 0 0 50rem var(--event-color);

  .cal-event__span,
  .cal-event__resource {
    background: rgba(255, 255, 255, 0.22);
  }
}

.cal-event--bar.cal-event--project:hover {
  background: color-mix(in srgb, var(--event-color) 22%, var(--cal-surface));
  color: color-mix(in srgb, var(--event-color) 70%, var(--cal-text));
  transform: none;
  box-shadow:
    0 1px 2px color-mix(in srgb, var(--event-color) 35%, transparent),
    0 0.375rem 0.875rem -0.25rem color-mix(in srgb, var(--event-color) 60%, transparent);
}
.cal-event-fade-enter-active,
.cal-event-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.35s cubic-bezier(0.22, 1, 0.36, 1) !important;
}

.cal-event-fade-enter-from,
.cal-event-fade-leave-to {
  opacity: 0 !important;
  transform: scale(0.92) !important;
}

.cal-event-fade-move {
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
</style>
<style scoped lang="scss">
.event-calendar {
  display: flex;
  gap: 1rem;
  height: calc(100vh - 6.5rem);
  min-height: 0;
  color: var(--cal-text);
}

.event-calendar--mobile {
  flex-direction: column;
  gap: 0;
  height: calc(100dvh - 3.5rem);
}

// --- Sidebar ---
.event-calendar__sidebar {
  flex-shrink: 0;
  width: 16.5rem;
  overflow: hidden;
  transition: width 0.25s ease, opacity 0.2s ease, margin 0.25s ease;
}

.event-calendar__sidebar--collapsed {
  width: 0;
  margin-right: -1rem;
  opacity: 0;
}

.event-calendar__sidebar-inner {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 16.5rem;
  height: 100%;
  overflow-y: auto;
  padding: 0.25rem 0.25rem 1rem 0;
  scrollbar-width: thin;
}

.event-calendar__sidebar-title {
  padding: 0 0.5rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--cal-text-muted);
}

// --- Main card ---
.event-calendar__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--cal-surface);
  border: 1px solid var(--cal-border);
  border-radius: $app-border-radius;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.event-calendar--mobile .event-calendar__main {
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.event-calendar__toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  padding: 0.5rem 0.75rem;
  background: var(--cal-surface-alt);
  border-bottom: 1px solid var(--cal-border);
}

.event-calendar__toolbar--mobile {
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.event-calendar__today-btn {
  padding: 0 0.75rem;
  font-size: 0.8125rem;
}

.event-calendar__nav {
  display: flex;
  border: 1px solid var(--cal-border);
  border-radius: 0.5rem;
  background: var(--cal-surface);
}

.event-calendar__title {
  margin: 0 0 0 0.25rem;
  overflow: hidden;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.01em;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-transform: capitalize;
}

.event-calendar__views {
  border: 1px solid var(--cal-border);
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--cal-surface);

  :deep(.q-btn) {
    padding: 0.125rem 0.75rem;
    font-size: 0.8125rem;
  }
}

.event-calendar__title-btn {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 0.25rem 0.25rem 0.25rem 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;

  .event-calendar__title {
    margin: 0;
    font-size: 1.25rem;
  }
}

.event-calendar__caret {
  transition: transform 0.25s ease;
}

.event-calendar__caret--open {
  transform: rotate(180deg);
}

.event-calendar__today-tile {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  width: 2.125rem;
  height: 2.25rem;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--cal-border);
  border-radius: 0.5rem;
  background: var(--cal-surface);
  color: var(--cal-text);
  font: inherit;
  box-shadow: 0 0.25rem 0.625rem -0.375rem color-mix(in srgb, var(--q-primary) 60%, transparent);
  cursor: pointer;
  transition: transform 0.15s ease;

  &:active {
    transform: scale(0.92);
  }
}

.event-calendar__today-band {
  padding: 0.0625rem 0;
  background: var(--q-primary);
  color: white;
  font-size: 0.5rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.event-calendar__today-day {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1;
}

.event-calendar__mobile-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: 2.25rem;
  padding: 0 0.125rem;
  border: 1px solid var(--cal-border);
  border-radius: 0.5rem;
  background: var(--cal-surface);
}

.event-calendar__mobile-action {
  height: 1.875rem;
  min-height: 0;
  padding: 0 0.5rem;
  border-radius: 0.375rem;
  color: var(--cal-text);
  font-size: 0.75rem;
  font-weight: 600;

  :deep(.q-btn__content) {
    flex-wrap: nowrap;
    gap: 0.25rem;
  }
}

.event-calendar__mobile-action-label {
  max-width: 5.5rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.event-calendar__mobile-action-caret {
  color: var(--cal-text-muted);
}

.event-calendar__mobile-action--icon {
  width: 1.875rem;
  padding: 0;
  font-size: 0.8125rem;
}

.event-calendar__mobile-action--active {
  background: color-mix(in srgb, var(--q-primary) 14%, transparent);
  color: var(--q-primary);
}

.event-calendar__mobile-divider {
  width: 1px;
  height: 1.125rem;
  margin: 0 0.125rem;
  background: var(--cal-border);
}

.event-calendar__view-menu {
  min-width: 12rem;
}

.event-calendar__menu {
  min-width: 15rem;
  max-width: 22rem;
}

.event-calendar__menu-header {
  padding: 0.5rem 1rem;
}

.event-calendar__menu-title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 600;
  color: var(--cal-text);
}

.event-calendar__menu-caption {
  font-size: 0.6875rem;
}

.event-calendar__menu-icon {
  min-width: 2rem;
}

.event-calendar__view-item--active {
  color: var(--q-primary);
  background: color-mix(in srgb, var(--q-primary) 10%, transparent);
  font-weight: 600;
}

.event-calendar__mini-mobile {
  padding: 0.5rem 0.75rem 0.75rem;
  background: var(--cal-surface-alt);
  border-bottom: 1px solid var(--cal-border);
}

// --- Stage & period transitions ---
.event-calendar__stage {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.event-calendar__loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 6;
}

.event-calendar__page {
  position: absolute;
  inset: 0;
  transition: transform 0.25s ease;
}

.cal-slide-next-enter-active,
.cal-slide-next-leave-active,
.cal-slide-prev-enter-active,
.cal-slide-prev-leave-active {
  transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.28s ease;
}

.cal-slide-next-enter-from,
.cal-slide-prev-leave-to {
  transform: translateX(6%);
  opacity: 0;
}

.cal-slide-next-leave-to,
.cal-slide-prev-enter-from {
  transform: translateX(-6%);
  opacity: 0;
}

.cal-title-enter-active,
.cal-title-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.cal-title-enter-from {
  transform: translateY(0.375rem);
  opacity: 0;
}

.cal-title-leave-to {
  transform: translateY(-0.375rem);
  opacity: 0;
}
</style>
